import React, { useState, useRef } from 'react'
import { Modal, Button, Spinner, Table, Form } from 'react-bootstrap'
import { uploadCsv } from '../utils/api'

export default function CSVUploader({ show, onHide, onSuccess = () => {} }) {
  const [file, setFile]             = useState(null)
  const [formData, setFormData]     = useState(null)
  const [uploading, setUploading]   = useState(false)
  const [message, setMessage]       = useState('')
  const [errors, setErrors]         = useState([])
  const [duplicates, setDuplicates] = useState([])
  const [decisions, setDecisions]   = useState({})
  const [resolving, setResolving]   = useState(false)
  const fileInputRef                = useRef()

  const resetFile = () => {
    setFile(null)
    setFormData(null)
    setDuplicates([])
    setErrors([])
    setDecisions({})
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileChange = e => {
    setMessage('')
    setErrors([])
    setDuplicates([])
    setDecisions({})

    const f = e.target.files[0]
    if (!f) {
      setFile(null)
      setFormData(null)
      return
    }

    const fd = new FormData()
    fd.append('file', f)

    setFile(f)
    setFormData(fd)
  }

  const handleUpload = async () => {
    if (!formData) return
    setUploading(true)
    setMessage('')
    setErrors([])
    setDuplicates([])

    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('override', 'false')

      const res = await uploadCsv(fd)
      console.log('CSV upload response:', res)

      if (res.errors?.length) {
        setErrors(res.errors)
      }
      if (Array.isArray(res.duplicates) && res.duplicates.length) {
        setDuplicates(res.duplicates)
      } else {
        setMessage('CSV uploaded successfully!')
        resetFile()
        onSuccess()
      }
    } catch (err) {
      console.error(err)
      setMessage(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDecisionChange = (idx, value) => {
    setDecisions(d => ({ ...d, [idx]: value }))
  }

  const handleResolve = async () => {
    setResolving(true)
    setMessage('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('override', 'true')
      fd.append('decisions', JSON.stringify(decisions))

      const res = await uploadCsv(fd)
      console.log('Resolve response:', res)
      if (res.errors?.length) setErrors(res.errors)
      if (res.duplicates?.length) {
        setDuplicates(res.duplicates)
      } else {
        setMessage(res.message || 'All rows processed!')
        resetFile()
        onSuccess()
      }
    } catch (err) {
      console.error('Resolve error:', err)
      setMessage(err.message || 'Failed to resolve duplicates')
    } finally {
      setResolving(false)
    }
  }

  return (
    <Modal className="csv-uploader-modal" show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="csv-uploader_title">Upload CSV</Modal.Title>
      </Modal.Header>
      <Modal.Body>

        <p className="csv-uploader_download-section mb-3">
          <a href="/faq_template.csv" download>
            <Button className="csv-uploader_download-btn" variant="outline-secondary" size="sm">
              📥 Download CSV Template
            </Button>
          </a>
        </p>

        <Form.Group controlId="csvFile" className="csv-uploader_file-select">
          <Form.Label>Select CSV file</Form.Label>
          <Form.Control
            className="csv-uploader_file-input"
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={uploading || resolving}
          />
        </Form.Group>

        <div className="csv-uplodaer_upload-section mt-3">
          <Button
          className="csv-uploader_upload-btn"
            variant="primary"
            onClick={handleUpload}
            disabled={!file || uploading || resolving}
          >
            {uploading
              ? <><Spinner as="span" animation="border" size="sm" /> Uploading…</>
              : 'Upload'}
          </Button>
        </div>

        {message && <p className="csv-uploader_message mt-3 text-success">{message}</p>}
        {errors.map((e, i) => (
          <p key={i} className="csv-uplodaer_error text-danger">
            Row {e.row}: {e.errors.join('; ')}
          </p>
        ))}

        {duplicates.length > 0 && (
          <div className="csv-uploader-duplicates">
            <h5 className="csv-uploader_duplicates-heading mt-4">Found {duplicates.length} duplicate rows</h5>
            <Table className="csv-uploader_duplicates-table" striped bordered size="sm">
              <thead>
                <tr>
                  <th>#</th><th>Question</th><th>Answer</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {duplicates.map((row, idx) => (
                  <tr key={idx}
                      className="csv-uploader_dublicates-row"
                  >
                    <td>{idx+1}</td>
                    <td>{row.question}</td>
                    <td>{row.answer}</td>
                    <td>
                      <Form.Check
                        className="csv-uplodaer_decision-radio"
                        inline type="radio" label="Keep both"
                        name={`decision-${idx}`} id={`keep-${idx}`}
                        checked={decisions[idx] === 'keep'}
                        onChange={() => handleDecisionChange(idx, 'keep')}
                      />
                      <Form.Check
                        className="csv-uploader_decision-radio"
                        inline type="radio" label="Overwrite"
                        name={`decision-${idx}`} id={`overwrite-${idx}`}
                        checked={decisions[idx] === 'overwrite'}
                        onChange={() => handleDecisionChange(idx, 'overwrite')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="csv-uploader_duplicates-action text-end">
              <Button
                className="csv-uploader_resolve-btn"
                variant="success"
                onClick={handleResolve}
                disabled={
                  resolving ||
                  Object.keys(decisions).length < duplicates.length
                }
              >
                {resolving
                  ? <><Spinner as="span" animation="border" size="sm" /> Saving…</>
                  : 'Apply Choices & Finish'}
              </Button>
            </div>
          </div>
        )}

      </Modal.Body>
      <Modal.Footer>
        <Button className="csv-uploader_close-btn" variant="secondary" onClick={onHide} disabled={uploading || resolving}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
